import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import * as styles from "./styles";
import { TaggedProfile, serviceLabel } from "./protocol";

const CARD_GAP_PX = 8;
const CARD_WIDTH_PX = 280;
const VIEWPORT_MARGIN_PX = 8;

// Service types as the scheduler's mention search sets them.
const kindLabel = ({ kind, service }: TaggedProfile): string | null => {
  if (kind === "profile") return "Person";
  if (kind === "page") return "Page";
  if (kind === "business") return service === "linkedin" ? "Company" : "Account";
  return null;
};

const Avatar: React.FC<{ person: TaggedProfile; size: number }> = ({ person, size }) => {
  const [failed, setFailed] = useState(false);
  const src = failed ? null : person.image;
  return (
    <styles.ProfileLogo $size={size} $hasImage={!!src} aria-hidden="true">
      {src ? <styles.TaggedAvatarImage src={src} alt="" onError={() => setFailed(true)} /> : person.name.trim().charAt(0).toUpperCase()}
    </styles.ProfileLogo>
  );
};

interface CardPosition {
  bottom: number;
  left: number;
}

// The card lives in a portal with fixed coordinates, because the chat thread scrolls and would clip it.
const cardPositionFor = (chip: DOMRect): CardPosition => ({
  bottom: window.innerHeight - chip.top + CARD_GAP_PX,
  left: Math.max(VIEWPORT_MARGIN_PX, Math.min(chip.left, window.innerWidth - CARD_WIDTH_PX - VIEWPORT_MARGIN_PX))
});

const TaggedChip: React.FC<{ person: TaggedProfile }> = ({ person }) => {
  const [position, setPosition] = useState<CardPosition | null>(null);
  const chipRef = useRef<HTMLButtonElement>(null);
  const details = [serviceLabel(person.service), kindLabel(person)].filter(Boolean).join(" · ");

  useEffect(() => {
    if (!position) return;
    const hide = () => setPosition(null);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [position]);

  const show = () => {
    if (chipRef.current) setPosition(cardPositionFor(chipRef.current.getBoundingClientRect()));
  };
  const hide = () => setPosition(null);

  return (
    <>
      <styles.TaggedChip
        ref={chipRef}
        type="button"
        aria-label={`Tagged ${person.name}${person.description ? `, ${person.description}` : ""}, ${details}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        <Avatar person={person} size={18} />
        <span className="name">{person.name}</span>
      </styles.TaggedChip>
      {position &&
        ReactDOM.createPortal(
          <styles.TaggedCard role="tooltip" style={{ ...position, width: CARD_WIDTH_PX }}>
            <Avatar person={person} size={40} />
            <styles.TaggedCardText>
              <strong>{person.name}</strong>
              <span>{person.description ?? "No description available"}</span>
              <small>{details}</small>
            </styles.TaggedCardText>
          </styles.TaggedCard>,
          document.body
        )}
    </>
  );
};

export const TaggedPeople: React.FC<{ people: TaggedProfile[] }> = ({ people }) => (
  <styles.TaggedList aria-label="Tagged in the caption">
    <span className="label">Tagged</span>
    {people.map((person, index) => (
      <TaggedChip key={`${index}-${person.service}`} person={person} />
    ))}
  </styles.TaggedList>
);
